import React, { ReactNode, useState } from 'react';
import { Autocomplete, Typography, ArrowDropDownIconSvg } from '@dev/ee-components';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ClickAwayListener, PaperProps } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import { getDomainsStore } from '@/store/domain/actions';
import { getValidEmailsStore } from '@/store/validemails/actions';

import { CustomPaper } from './components/CustomPaper';
import { EmailDropdownType, EmailDropdownOption } from './types';
import { useGetEmailsOptions } from './hooks/useGetEmailDropdownOptions';

const GroupHeader = styled.div`
    padding-top: 12px;
    padding-left: 16px;
`;

const IconWrapper = styled.span`
    width: 20px;
    height: 20px;
`;

const Wrapper = styled.div`
    display: flex;
`;

const AutocompleteWrapper = styled.div`
    flex-grow: 1;
    .MuiInputBase-root {
        padding-right: 32px;
    }
`;

type GroupParams = {
    key: string;
    group: string;
    children: React.ReactNode | React.ReactNode[];
};

export const EmailDropdown = ({ 
    label, 
    setEmail, 
    email, 
    hasError, 
    id, 
    errorMessage, 
    placeholder,
    readOnly
}: EmailDropdownType): JSX.Element => {
    const { domains, loadingDomains } = useSelector(getDomainsStore);
    const { emails, loadingEmails } = useSelector(getValidEmailsStore);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [isSearchVisible, setSearchVisible] = useState(false);

    const dropdownOptions = useGetEmailsOptions();

    const isLoading = loadingDomains || loadingEmails;
    const isSendersAlreadyLoaded = !!emails && !!domains;


    const getFilterOptions = (options: EmailDropdownOption[]) => {
        const results: EmailDropdownOption[] = [...options];
        let filteredResults = results;

        if (!!search && search !== '') {
            filteredResults = results.filter((e) => e.name.includes(search));
        }
        if (!!options?.length && options?.length > 20) {
            setSearchVisible(true);
        }
        return filteredResults;
    };

    const renderGroup = (params: GroupParams): ReactNode[] => [
        <GroupHeader key={params.key}>
            <Typography variant="body2">{params.group}</Typography>
        </GroupHeader>,
        params.children,
    ];

    const handleOnChange = (): void => {
        const inputSelector = `#${id}`;
        const input = document.querySelector<HTMLInputElement>(inputSelector);
        setTimeout(() => {
            input.setSelectionRange(0, 0);
        }, 0);
    };

    const handleOnInputChange = (email: string): void => {
        setEmail(email);
        setOpen(false);
    };
 
    const handleOnClickAway = (): void => {
        setOpen(false);
    };

    const renderCustomPaper = (paperProps: PaperProps): JSX.Element => (
        <CustomPaper
            {...paperProps}
            isSearchVisible={isSearchVisible}
            handleSearchInput={(e) => setSearch(e.target.value)}
            searchValue={search}
            handleClearSearch={() => setSearch('')}
        />
    );

    return isLoading && !isSendersAlreadyLoaded ? (
        <Skeleton style={{ width: '100%', height: 44, marginTop: 20 }} />
    ) : (
        <ClickAwayListener onClickAway={handleOnClickAway}>
            <div key="click-away-wrapper">
                <Wrapper>
                    <AutocompleteWrapper>
                        <Autocomplete
                            id={id}
                            defaultValue={{
                                name: email,
                                value: email,
                            }}
                            inputValue={email}
                            options={dropdownOptions}
                            freeSolo
                            disableClearable
                            placeholder={placeholder}
                            popupIcon={
                                <IconWrapper onClick={() => setOpen(!open)}>
                                    <ArrowDropDownIconSvg />
                                </IconWrapper>
                            }
                            forcePopupIcon
                            onChange={handleOnChange}
                            onInputChange={(e, value) => handleOnInputChange(value)}
                            label={label}
                            groupBy={(option) => option.group}
                            getOptionLabel={(option) => option.value}
                            filterOptions={getFilterOptions}
                            renderOption={(option) => <Typography variant="body1">{option.name}</Typography>}
                            renderGroup={renderGroup}
                            PaperComponent={renderCustomPaper}
                            open={open}
                            error={hasError}
                            errorMessage={errorMessage}
                            readOnly={readOnly}
                        />
                    </AutocompleteWrapper>
                </Wrapper>
            </div>
        </ClickAwayListener>
    );
};
